import { pick } from './utils';
import PropTypes from 'prop-types';
import isEqual from 'fast-deep-equal';
import React, { useEffect, useRef } from 'react';
import { bind, clear } from 'size-sensor';

const EchartsReactCore = (props) => {
  const {
    option,
    echarts,
    notMerge,
    lazyUpdate,
    style,
    className,
    theme,
    onChartReady,
    showLoading,
    loadingOption,
    onEvents,
    opts,
    shouldSetOption,
  } = props;

  const echartsElement = useRef(null);
  // const [echartsLib] = echarts;

  const getEchartsInstance = () => {
    return echarts.getInstanceByDom(echartsElement.current) ||
    echarts.init(echartsElement.current, theme, opts);
  };

  const dispose = () => {
    if (echartsElement.current) {
      try {
        clear(echartsElement.current);
      } catch (e) {
        console.warn(e);
      }
      echarts.dispose(echartsElement.current);
    }
  };

  const bindEvents = (instance, events) => {
    const _bindEvent = (eventName, func) => {
      if (typeof eventName === 'string' && typeof func === 'function') {
        instance.on(eventName, (param) => {
          func(param, instance);
        });
      }
    };

    for (const eventName in events) {
      if (Object.prototype.hasOwnProperty.call(events, eventName)) {
        _bindEvent(eventName, events[eventName]);
      }
    }
  };

  const renderEchartDom = () => {
    const echartObj = getEchartsInstance();
    echartObj.setOption(option, notMerge || false, lazyUpdate || false);
    if (showLoading) echartObj.showLoading(loadingOption || null);
    else echartObj.hideLoading();

    return echartObj;
  };

  const rerender = () => {
    const echartObj = renderEchartDom();
    bindEvents(echartObj, onEvents || {});

    if (typeof onChartReady === 'function') onChartReady(echartObj);
    if (echartsElement.current) {
      bind(echartsElement.current, () => {
        try {
          echartObj.resize();
        } catch (e) {
          console.warn(e);
        }
      });
    }
  };

  useEffect(() => {
    rerender();
    return () => {
      dispose();
    };
  }, []);

  useEffect(() => {
    const prevProps = {
      theme: null,
      opts: null,
      onEvents: null,
    };

    if (
      !isEqual(prevProps.theme, theme) ||
      !isEqual(prevProps.opts, opts) ||
      !isEqual(prevProps.onEvents, onEvents)
    ) {
      dispose();
      rerender();
      return;
    }

    const pickKeys = ['option', 'notMerge', 'lazyUpdate', 'showLoading', 'loadingOption'];
    if (isEqual(pick(props, pickKeys), pick(prevProps, pickKeys))) {
      return;
    }

    if (
      typeof shouldSetOption === 'function' &&
      !shouldSetOption(prevProps, props)
    ) {
      return;
    }

    const echartObj = renderEchartDom();
    if (
      !isEqual(prevProps.style, style) ||
      !isEqual(prevProps.className, className)
    ) {
      try {
        echartObj.resize();
      } catch (e) {
        console.warn(e);
      }
    }
  }, [theme, opts, onEvents, option, notMerge, lazyUpdate, showLoading, loadingOption, style, className, shouldSetOption]);

  const newStyle = {
    height: 300,
    ...style,
  };

  return (
    <div
      ref={echartsElement}
      style={newStyle}
      className={`echarts-for-react ${className}`}
    />
  );
};

EchartsReactCore.propTypes = {
  option: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  echarts: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  notMerge: PropTypes.bool,
  lazyUpdate: PropTypes.bool,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  className: PropTypes.string,
  theme: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChartReady: PropTypes.func,
  showLoading: PropTypes.bool,
  loadingOption: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onEvents: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  opts: PropTypes.shape({
    devicePixelRatio: PropTypes.number,
    renderer: PropTypes.oneOf(['canvas', 'svg']),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined, 'auto'])]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined, 'auto'])])
  }),
  shouldSetOption: PropTypes.func
};

EchartsReactCore.defaultProps = {
  echarts: {},
  notMerge: false,
  lazyUpdate: false,
  style: {},
  className: '',
  theme: null,
  onChartReady: () => { },
  showLoading: false,
  loadingOption: null,
  onEvents: {},
  opts: {},
  shouldSetOption: () => true
};

export default EchartsReactCore;